'use client';

import { ordersQueryOptions } from '@/api/options';
import { Button, CTA, Input } from '@/components';
import { useMeQuery } from '@/entities/auth';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import {
  Column,
  Element,
  FullScreenOverlayLoadingSpinner,
  Icon,
  Row,
} from '@vuddy/components';
import { isEmpty } from 'lodash-es';
import { KeyboardEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './verificationCodeModal.css';

const MAX_LENGTH = 15;

const noSpaceUpper = (str: string) => str.replace(/\s+/g, '').toUpperCase();

interface VerificationCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (codeName: string) => void;
  productId: number;
  /**
   * 검증 완료된 코드만 전달합니다.
   * - 값이 있으면: 재진입 시 입력값 유지
   * - 값이 없으면: 재진입 시 입력 필드 초기화
   */
  initialCode?: string;
}

export const VerificationCodeModal = ({
  isOpen,
  onClose,
  onVerified,
  productId,
  initialCode,
}: VerificationCodeModalProps) => {
  const [inputValue, setInputValue] = useState('');
  const [errorValue, setErrorValue] = useState('');
  const [isPending, setIsPending] = useState(false);
  const device = useDeviceSize();
  const isMobile = device === 'mobile';
  const { data: meData } = useMeQuery();
  const userId = meData?.id;

  useEffect(() => {
    if (!isOpen) return;
    // 모달을 닫더라도 컴포넌트가 언마운트되지 않아 state가 유지되므로,
    // 오픈 시점에 항상 현재 상품 기준으로 입력/에러를 동기화한다.
    setErrorValue('');
    setInputValue(initialCode ?? '');
  }, [isOpen, productId, initialCode]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (isEmpty(inputValue)) return;
    if (!userId) {
      setErrorValue('로그인이 필요합니다.');
      return;
    }

    setIsPending(true);
    setErrorValue('');

    try {
      const response = await ordersQueryOptions
        .verifyPromotionCodeOrder({
          payload: {
            userId,
            productId,
            promotionCode: inputValue,
          },
        })
        .queryFn();

      if (!response.isValid) {
        setErrorValue(response.errorMessage || '유효하지 않은 코드입니다.');
        setIsPending(false);
        return;
      }

      setIsPending(false);
      onVerified(inputValue);
    } catch {
      setErrorValue('코드 검증 중 오류가 발생했습니다.');
      setIsPending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const inputElement = (
    <Input
      placeholder="코드를 입력해주세요."
      maxLength={MAX_LENGTH}
      value={inputValue}
      error={!isEmpty(errorValue)}
      errorText={errorValue}
      onChange={e => setInputValue(noSpaceUpper(e.target.value))}
      onKeyDown={handleKeyDown}
      onClear={() => {
        setInputValue('');
        setErrorValue('');
      }}
    />
  );

  const submitButton = (
    <Button
      size="medium"
      variant="solidPrimary"
      onClick={handleSubmit}
      disabled={isEmpty(inputValue) || isPending}
      fullWidth>
      확인
    </Button>
  );

  const mobileContent = (
    <div className={styles.overlayMobile}>
      <Column className={styles.containerMobile}>
        {/* Header */}
        <Row
          alignItems="center"
          justifyContent="center"
          paddingY="padding_small"
          paddingX="padding_small"
          className={styles.header}>
          <button className={styles.closeButtonMobile} onClick={onClose}>
            <Icon name="OUTLINE_CHEVRON_LEFT" size="24px" />
          </button>
          <Element typography="titleSmall">코드 입력</Element>
        </Row>

        {/* Content */}
        <FullScreenOverlayLoadingSpinner condition={!isPending}>
          <Column className={styles.contentMobile}>{inputElement}</Column>
          <CTA direction="vertical" position="sticky">
            {submitButton}
          </CTA>
        </FullScreenOverlayLoadingSpinner>
      </Column>
    </div>
  );

  const desktopContent = (
    <div className={styles.overlayDesktop} onClick={onClose}>
      <Column
        className={styles.containerDesktop}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <Row
          alignItems="center"
          justifyContent="center"
          paddingY="padding_small"
          paddingX="padding_small"
          className={styles.header}>
          <Element typography="titleSmall">코드 입력</Element>
          <button className={styles.closeButtonDesktop} onClick={onClose}>
            <Icon name="OUTLINE_CLOSE" size="24px" />
          </button>
        </Row>

        {/* Content */}
        <FullScreenOverlayLoadingSpinner condition={!isPending}>
          <Column className={styles.contentDesktop}>
            <Element className={styles.formDesktop}>{inputElement}</Element>
            <Element className={styles.ctaDesktop}>{submitButton}</Element>
          </Column>
        </FullScreenOverlayLoadingSpinner>
      </Column>
    </div>
  );

  return createPortal(isMobile ? mobileContent : desktopContent, document.body);
};
